import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { fromEvent, Subject, Subscription } from 'rxjs';

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input,
  OnDestroy, OnInit, Output, QueryList, Renderer2, ViewChild, ViewChildren, ViewEncapsulation
} from '@angular/core';

import {
  BrandThemesEnum, DeviceService, fadeInOutAnimation, ImageRatioEnum, lBreakpoint,
  SdBannerComponent, SdListComponent, SdThemeComponent, ThemesEnum
} from '@seb/web-design-system/core';

import type { IAccordionImage, IAccordionImageStatus } from './accordion-image/accordion-image.interface';
import { SdAccordionImageComponent } from './accordion-image/accordion-image.component';

@Component({
  selector: 'sd-accordion-group-image',
  templateUrl: './accordion-group-image.component.html',
  styleUrls: ['./accordion-group-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  animations: [
    fadeInOutAnimation({
      anchor: 'fadeInOutBg',
      durationIn: 0,
      delayIn: 0,
      durationOut: 250,
      delayOut: 350
    })
  ],
  imports: [
    SdListComponent,
    SdAccordionImageComponent,
    SdBannerComponent
  ]
})
export class SdAccordionGroupImageComponent extends SdThemeComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input()
  isLoading = false;

  @Input()
  slices: IAccordionImage[];

  @Input()
  isSmall = false;

  @Input()
  theme: ThemesEnum.dark | ThemesEnum.light | BrandThemesEnum.brand = ThemesEnum.dark;

  @Output()
  updateBackgroundStyles = new Subject<number>();

  @ViewChild('slicesContainer')
  slicesContainer: ElementRef;

  @ViewChildren('sliceElement')
  sliceElements: QueryList<ElementRef>;

  @ViewChild('sliceBackground')
  sliceBackground: ElementRef;

  @Output()
  sliceClosed = new EventEmitter<IAccordionImage>();

  @Output()
  sliceOpened = new EventEmitter<IAccordionImage>();

  @Output()
  urlClicked = new EventEmitter<IAccordionImage>();

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.isSmall ? 'is-small' : '',
      this.theme ? 'is-theme-' + this.theme : '',
      this.hasAnimation ? 'has-animation' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  currentOpenSlice: number;
  subscriptions = new Subscription();
  isDevice$: boolean;
  cssFlexGrow = 6; // flex-grow set for open slice
  countImageLoaded = 0;
  isMobile = false;
  imageRatioEnum = ImageRatioEnum;
  hasAnimation = false;

  constructor(
    private viewportScroller: ViewportScroller,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile = x;
        this.cdRef.markForCheck();
      })
    );

    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        fromEvent(window, 'resize').subscribe(() => {
          if (window.innerWidth > lBreakpoint) {
            this.closeSlice();
          }
          this.updateSlicesBackground();
        })
      );
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.resetSlices(false);
      this.updateSlicesBackground();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  closeSlice(event?: IAccordionImageStatus): void {
    if (!!event && event.close) {
      this.sliceClosed.emit(this.slices[event.sliceIndex]);
    }

    this.currentOpenSlice = null;
    this.resetSlices(false);

    if (this.isDevice$) {
      setTimeout(() => {
        this.viewportScroller.scrollToPosition([0, this.document.scrollingElement.scrollTop]);
        this.renderer.setStyle(this.sliceBackground?.nativeElement, 'height', '100%');
      });
    }
  }

  openSlice(event: IAccordionImageStatus): void {
    if (!this.hasAnimation) {
      this.hasAnimation = true;
    }

    if (event.open) {
      this.sliceOpened.emit(this.slices[event.sliceIndex]);

      if (this.currentOpenSlice) {
        this.sliceClosed.emit(this.slices[this.currentOpenSlice]);
      }
    }

    this.currentOpenSlice = event.sliceIndex;
    this.updateSliceStatus(event);

    if (this.isDevice$) {
      setTimeout(() => {
        this.viewportScroller.scrollToPosition([0, this.document.scrollingElement.scrollTop]);
      });
    }
  }

  openUrl(): void {
    if (this.currentOpenSlice >= 0) {
      this.urlClicked.emit(this.slices[this.currentOpenSlice]);
    }
  }

  imageLoaded(): void {
    if (++this.countImageLoaded === this.slices?.length) {
      setTimeout(() => {
        this.updateSlicesBackground();
      }, 500);
    }
  }

  private updateSlicesBackground(): void {
    if (!this.isDevice$) {
      this.slices?.forEach((el: IAccordionImage, index: number) => {
        el.sliceSize = this.sliceElements?.toArray()[index].nativeElement.offsetWidth;
      });
      this.updateBackgroundStyles.next(
        this.slicesContainer?.nativeElement.offsetWidth,
      );
      this.centerSliceButton();
    }
  }

  private resetSlices(isSlice: boolean): void {
    this.slices?.forEach((el) => {
      el.isSlice = isSlice;
      el.isOpen = false;
    });
  }

  private updateSliceStatus(event: IAccordionImageStatus): void {
    this.resetSlices(true);
    this.slices[event.sliceIndex].isOpen = true;
    this.slices[event.sliceIndex].isSlice = false;
  }

  private centerSliceButton(): void {
    setTimeout(() => {
      /* Center slice text button */
      this.sliceElements?.forEach((sliceEl) => {
        const sliceHeader = sliceEl?.nativeElement.querySelector('.c__header-slice');
        const sliceHeaderButton = sliceHeader?.querySelector('button');

        const sliceWidth =
          (1 / (this.cssFlexGrow + (this.sliceElements?.length - 1))) *
          this.slicesContainer?.nativeElement?.offsetWidth;

        if (sliceHeader) {
          this.renderer.setStyle(sliceHeader, 'display', 'block');
        }

        const calcCenterButton = window.innerWidth > lBreakpoint ? (sliceWidth - sliceHeaderButton?.offsetHeight) / 2 : 0;

        if (sliceHeaderButton) {
          this.renderer.setStyle(
            sliceHeaderButton,
            'margin-left',
            calcCenterButton + 'px',
          );
        }

        if (sliceHeader) {
          this.renderer.setStyle(sliceHeader, 'display', 'none');
        }
      });
    }, 500);
  }
}
